/**
 * HoursInput Component
 *
 * Shared hours input with slider, number input, and overtime checkbox.
 * Uses data attributes for styling variants.
 *
 * The text input uses local string state to allow typing decimal values
 * (e.g., "4.5") without immediate parsing that would destroy intermediate
 * states like "4.". Value is synced to parent on blur or valid input.
 *
 * @example
 * ```tsx
 * // Full variant with slider
 * <HoursInput
 *   value={8}
 *   maxHours={16}
 *   defaultHours={8}
 *   allowOvertime={false}
 *   onChange={handleSliderChange}
 *   onInputChange={handleInputChange}
 *   onOvertimeToggle={handleOvertimeToggle}
 *   testIdPrefix="add-form"
 * />
 *
 * // Simple variant (just input, no slider)
 * <HoursInput
 *   variant="simple"
 *   value={8}
 *   onChange={handleSliderChange}
 *   onInputChange={handleInputChange}
 *   testIdPrefix="edit-form"
 * />
 * ```
 */

import React, { useState, useEffect, useCallback } from "react";
import classNames from "classnames";
import { Checkbox } from "@/components/ui/forms/Checkbox";
import { Slider } from "@/components/ui/forms/Slider";
import { generateId } from "@/lib/utils";
import styles from "./HoursInput.module.css";

export interface HoursInputProps {
  /** Current hours value */
  value: number;
  /** Handler for slider change */
  onChange: (value: number) => void;
  /** Handler for number input change */
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Prefix for test IDs (e.g., "add-form" or "edit-form") */
  testIdPrefix: string;
  /** Variant: "full" shows slider + overtime, "simple" shows just input */
  variant?: "full" | "simple";
  /** Maximum hours allowed (for slider) */
  maxHours?: number;
  /** Default hours per day (for overtime label) */
  defaultHours?: number;
  /** Whether overtime is allowed */
  allowOvertime?: boolean;
  /** Handler for overtime toggle */
  onOvertimeToggle?: () => void;
  /** Optional additional CSS class */
  className?: string;
}

export const HoursInput: React.FC<HoursInputProps> = ({
  value,
  onChange,
  onInputChange,
  testIdPrefix,
  variant = "full",
  maxHours = 8,
  defaultHours = 8,
  allowOvertime = false,
  onOvertimeToggle,
  className,
}) => {
  // Local string state for text input to allow typing decimals
  // This prevents "4." from being immediately parsed to "4"
  const [inputValue, setInputValue] = useState(String(value));

  // Sync local state when external value changes (e.g., from slider)
  useEffect(() => {
    // Only update if the parsed values are different
    // This preserves typing state when the value hasn't actually changed
    const parsed = parseFloat(inputValue.replace(",", "."));
    if (isNaN(parsed) || parsed !== value) {
      setInputValue(String(value));
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle text input change - keep string locally, sync valid numbers to parent
  const handleLocalInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;
      setInputValue(rawValue);

      // Parse and sync to parent if valid (supports both . and , as decimal separator)
      const parsed = parseFloat(rawValue.replace(",", "."));
      if (!isNaN(parsed) && parsed > 0) {
        // Only call parent handler for complete valid numbers
        // Check if the string ends with a decimal point or comma - don't sync yet
        if (!rawValue.endsWith(".") && !rawValue.endsWith(",")) {
          onInputChange(e);
        }
      }
    },
    [onInputChange],
  );

  // Handle blur - ensure parent gets final value
  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const parsed = parseFloat(inputValue.replace(",", "."));
      if (!isNaN(parsed) && parsed > 0) {
        // Create a synthetic event with the cleaned value for parent
        const syntheticEvent = {
          ...e,
          target: { ...e.target, value: String(parsed) },
        } as React.ChangeEvent<HTMLInputElement>;
        onInputChange(syntheticEvent);
      } else {
        // Invalid input - reset to current value
        setInputValue(String(value));
      }
    },
    [inputValue, value, onInputChange],
  );

  // Simple variant - just label and input
  if (variant === "simple") {
    return (
      <div
        className={classNames(styles.hoursInput, className)}
        data-variant="simple"
      >
        <label data-element="label">Timer</label>
        <input
          type="text"
          inputMode="decimal"
          value={inputValue}
          onChange={handleLocalInputChange}
          onBlur={handleBlur}
          data-element="input"
          data-testid={`${testIdPrefix}-hours`}
        />
      </div>
    );
  }

  // Full variant - slider + input + overtime checkbox
  return (
    <div
      className={classNames(styles.hoursInput, className)}
      data-variant="full"
    >
      <div data-element="slider">
        <Slider
          label="Timer"
          value={value}
          onChange={onChange}
          min={0.5}
          max={maxHours}
          step={0.5}
          formatValue={(v) => `${v}t`}
          data-testid={`${testIdPrefix}-slider`}
        />
      </div>
      <input
        type="text"
        inputMode="decimal"
        value={inputValue}
        onChange={handleLocalInputChange}
        onBlur={handleBlur}
        data-element="input"
        data-testid={`${testIdPrefix}-hours`}
      />
      {onOvertimeToggle && (
        <div data-element="overtime">
          <Checkbox
            id={generateId(`${testIdPrefix}-overtime`)}
            checked={allowOvertime}
            onChange={onOvertimeToggle}
            label={`Over ${defaultHours} timer`}
            data-testid={`${testIdPrefix}-overtime`}
          />
        </div>
      )}
    </div>
  );
};

HoursInput.displayName = "HoursInput";

export default HoursInput;
